// Morehouse College: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Morehouse's newest posted Common Data Set is 2023-2024 (Data Analytics,
//    Institutional Research and Effectiveness, Common Data Set page, split into
//    section PDFs; its C1 reports Fall 2023), and the newest Fact Book is also
//    2023-2024. A look through morehouse.edu's sitemap found nothing newer, so
//    the CDS is not used. The first-year counts come from the U.S. Department
//    of Education's IPEDS Admissions survey for Fall 2024, first-time
//    degree-seeking students (College Navigator and the ADM2024 complete data
//    file, unit 140553). IPEDS gives no residency split and no early round
//    counts, so this page has neither.
//  - morehouse.edu Admissions: the Fall 2027 Admissions Key Dates on the
//    admissions home page, Requirements and Deadlines (what Early Decision
//    and Early Action mean), and First-time Freshmen (test scores optional).
//    Quirk: Requirements and Deadlines lists "Official SAT/ACT scores" among
//    the application materials without marking them optional, while the
//    First-time Freshmen and International Freshmen pages mark them optional;
//    this page follows the First-time Freshmen page and says so.
//  - Morehouse Student Accounts, Cost of Attendance: the 2026-2027 standard
//    budget for on-campus students.
//  - College Scorecard API, school id 140553 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=140553#admsns',
        read: '2026-10-01',
    },
    cdsPage: {
        label: 'Morehouse Institutional Research, Common Data Set page (newest edition 2023-2024)',
        url: 'https://morehouse.edu/about/offices-and-services/data-analytics-institutional-research-and-effectiveness/common-data-set/',
        read: '2026-10-01',
    },
    admissions: {
        label: 'Morehouse Admissions, Fall 2027 Admissions Key Dates',
        url: 'https://morehouse.edu/admissions',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Morehouse Admissions, Requirements and Deadlines',
        url: 'https://morehouse.edu/admissions/requirements-and-deadlines',
        read: '2026-10-01',
    },
    freshmen: {
        label: 'Morehouse Admissions, First-time Freshmen',
        url: 'https://morehouse.edu/admissions/apply/first-time-freshmen',
        read: '2026-10-01',
    },
    coa: {
        label: 'Morehouse Student Accounts, Cost of Attendance 2026-2027',
        url: 'https://morehouse.edu/aid/student-accounts/cost-of-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?140553-Morehouse-College',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'morehouse-college',
    kbId: 'morehouse_college',
    name: 'Morehouse College',
    shortName: 'Morehouse',
    location: 'Atlanta, GA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Morehouse brand guidelines and logos); decoration only.
    branding: {
        primary: '#840028',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/morehouse_college_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 5733,
        admitted: 2520,
        enrolled: 684,
        source: 'ipeds',
        where: 'first-time, degree-seeking first-year students',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Morehouse\'s newest posted Common Data Set is the 2023-2024 edition, with Fall 2023 counts, so the counts above come from the Department of Education\'s IPEDS survey for Fall 2024, which gives one first-year rate for the whole college and none by major.',
        source: 'cdsPage',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'December 5', binding: true },
            { plan: 'Early Action', deadline: 'December 15', decision: 'January 29', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'March 12', binding: false },
        ],
        note: 'The application for fall 2027 admission opened August 1, 2026. The financial aid deadline is January 30, 2027 for every round. Students admitted Early Decision pay their deposit by January 15, 2027; Early Action and Regular Decision admits by May 1, 2027. International applicants follow the Regular Decision dates.',
        source: 'admissions',
        binding: 'Early Decision is binding, for students who know Morehouse is their top choice. Early Action is not: you apply by December 15 and hear sooner, without a commitment to enroll.',
        bindingSource: 'requirements',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Morehouse lists official SAT or ACT scores as optional for first-time freshmen. Its Requirements and Deadlines page still lists official SAT/ACT scores among the application materials without that label, so ask admissions if you are unsure.',
        source: 'freshmen',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'admissions',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 31026 },
            { item: 'Student fees', amount: 4258 },
            { item: 'Food', amount: 4092 },
            { item: 'Housing', amount: 11290 },
            { item: 'Books and supplies', amount: 2000 },
            { item: 'Transportation', amount: 2880 },
            { item: 'Loan fees', amount: 200 },
            { item: 'Personal and miscellaneous', amount: 3684 },
        ],
        total: 59430,
        note: 'For a student living on campus and enrolled in six or more hours, from the standard cost of attendance the Office of Financial Aid and Scholarships publishes for 2026-2027.',
        source: 'coa',
        netPrice: {
            average: 38655,
            byIncome: [
                { income: '$0 to $30,000', amount: 32102 },
                { income: '$30,001 to $48,000', amount: 32502 },
                { income: '$48,001 to $75,000', amount: 34353 },
                { income: '$75,001 to $110,000', amount: 40330 },
                { income: 'Over $110,000', amount: 44140 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Morehouse\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
